import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import {
  useIndexPageFilterSettings,
  useResetIndexPageFilters,
  useSetIndexPageFilterVisibility,
} from 'src/pages/AccountSettings/AccountSettings.hooks'

export type FilterConfigurablePage =
  | INDEX_PAGE_WITH_FILTERS.CONTROLS
  | INDEX_PAGE_WITH_FILTERS.POLICIES
  | INDEX_PAGE_WITH_FILTERS.RISKS
  | INDEX_PAGE_WITH_FILTERS.VENDORS

export const CONFIGURABLE_FILTER_PAGES: FilterConfigurablePage[] = [
  INDEX_PAGE_WITH_FILTERS.CONTROLS,
  INDEX_PAGE_WITH_FILTERS.POLICIES,
  INDEX_PAGE_WITH_FILTERS.RISKS,
  INDEX_PAGE_WITH_FILTERS.VENDORS,
]

export type FilterHideListState = {
  [page in FilterConfigurablePage]?: Record<string, boolean>
}

export interface FilterHideListContextValue {
  hideList: FilterHideListState
  isFilterHidden: (page: string, filterKey: string) => boolean
  /**
   * Applies the change optimistically and resolves once it is persisted.
   * Rejects - after rolling the optimistic change back - when the write fails,
   * so callers can report the failure instead of claiming success.
   */
  setFilterHidden: (
    page: string,
    filterKey: string,
    hidden: boolean,
  ) => Promise<void>
  resetPageFilters: (page: string) => Promise<void>
  resetAllFilters: () => Promise<void>
  getHiddenFilterCount: (page: string) => number
  isLoading: boolean
}

const FilterHideListContext = createContext<FilterHideListContextValue>({
  hideList: {},
  isFilterHidden: () => false,
  setFilterHidden: () => Promise.resolve(),
  resetPageFilters: () => Promise.resolve(),
  resetAllFilters: () => Promise.resolve(),
  getHiddenFilterCount: () => 0,
  isLoading: false,
})

const ALL_PAGES_WRITE_KEY = '*'

export const FilterHideListProvider = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  const { data, loading: isLoading } = useIndexPageFilterSettings()
  const setVisibilityApi = useSetIndexPageFilterVisibility()
  const resetFiltersApi = useResetIndexPageFilters()

  const [localHideList, setLocalHideList] = useState<FilterHideListState>({})
  // Mirrors the state so an optimistic write can read the value it is replacing
  // without going through a setState updater.
  const hideListRef = useRef<FilterHideListState>(localHideList)
  const pendingWrites = useRef<Map<string, Promise<void>>>(new Map())

  const applyHideList = useCallback((next: FilterHideListState) => {
    hideListRef.current = next
    setLocalHideList(next)
  }, [])

  const applyFilterHidden = useCallback(
    (page: FilterConfigurablePage, filterKey: string, hidden: boolean) => {
      const current = hideListRef.current
      const pageList = { ...(current[page] || {}) }
      if (hidden) {
        pageList[filterKey] = true
      } else {
        delete pageList[filterKey]
      }
      applyHideList({ ...current, [page]: pageList })
    },
    [applyHideList],
  )

  // Writes sharing a key are chained, so rapid opposite toggles of the same
  // filter land in the order they were made - the last intent wins rather than
  // whichever request happens to reach the server first.
  const queueWrite = useCallback(
    (key: string, write: () => Promise<void>): Promise<void> => {
      const previous = pendingWrites.current.get(key) ?? Promise.resolve()
      const next = previous.catch(() => undefined).then(write)
      pendingWrites.current.set(key, next)
      next
        .catch(() => undefined)
        .then(() => {
          if (pendingWrites.current.get(key) === next) {
            pendingWrites.current.delete(key)
          }
        })
      return next
    },
    [],
  )

  useEffect(() => {
    if (!data) return
    const list: FilterHideListState = {}
    const items = data.getItemsList()
    items.forEach((item) => {
      const page = item.getPage() as FilterConfigurablePage
      const filterKey = item.getFilterKey()
      const hidden = item.getHidden()
      if (hidden) {
        const pageMap = list[page] ?? {}
        pageMap[filterKey] = true
        list[page] = pageMap
      }
    })
    applyHideList(list)
  }, [data, applyHideList])

  const isFilterHidden = useCallback(
    (page: string, filterKey: string): boolean => {
      const pageKey = page as FilterConfigurablePage
      return Boolean(localHideList[pageKey]?.[filterKey])
    },
    [localHideList],
  )

  const setFilterHidden = useCallback(
    (page: string, filterKey: string, hidden: boolean): Promise<void> => {
      const pageKey = page as FilterConfigurablePage
      const previousHidden = Boolean(hideListRef.current[pageKey]?.[filterKey])
      applyFilterHidden(pageKey, filterKey, hidden)

      return queueWrite(`${page}:${filterKey}`, () =>
        setVisibilityApi(page, filterKey, hidden),
      ).catch((err) => {
        // Roll back only this filter so toggles that did persist are kept.
        applyFilterHidden(pageKey, filterKey, previousHidden)
        throw err
      })
    },
    [applyFilterHidden, queueWrite, setVisibilityApi],
  )

  const resetPageFilters = useCallback(
    (page: string): Promise<void> => {
      const pageKey = page as FilterConfigurablePage
      const previousList = hideListRef.current
      const next = { ...previousList }
      delete next[pageKey]
      applyHideList(next)

      return queueWrite(page, () => resetFiltersApi(page)).catch((err) => {
        applyHideList({
          ...hideListRef.current,
          [pageKey]: previousList[pageKey] ?? {},
        })
        throw err
      })
    },
    [applyHideList, queueWrite, resetFiltersApi],
  )

  const resetAllFilters = useCallback((): Promise<void> => {
    const previousList = hideListRef.current
    applyHideList({})

    return queueWrite(ALL_PAGES_WRITE_KEY, () => resetFiltersApi()).catch(
      (err) => {
        applyHideList(previousList)
        throw err
      },
    )
  }, [applyHideList, queueWrite, resetFiltersApi])

  const getHiddenFilterCount = useCallback(
    (page: string): number => {
      const pageKey = page as FilterConfigurablePage
      const pageList = localHideList[pageKey]
      if (!pageList) return 0
      return Object.values(pageList).filter(Boolean).length
    },
    [localHideList],
  )

  const value = useMemo(
    () => ({
      hideList: localHideList,
      isFilterHidden,
      setFilterHidden,
      resetPageFilters,
      resetAllFilters,
      getHiddenFilterCount,
      isLoading,
    }),
    [
      localHideList,
      isFilterHidden,
      setFilterHidden,
      resetPageFilters,
      resetAllFilters,
      getHiddenFilterCount,
      isLoading,
    ],
  )

  return (
    <FilterHideListContext.Provider value={value}>
      {children}
    </FilterHideListContext.Provider>
  )
}

export const useFilterHideList = (): FilterHideListContextValue =>
  useContext(FilterHideListContext)
